import { useState } from 'react';

interface AuditRecord {
  auditId: string;
  timestamp: string;
  userName: string;
  action: string;
  itemCount: number;
  year: number;
}

interface ImportHistoryProps {
  teamId: string;
  history: AuditRecord[];
  isLoading: boolean;
  canEdit: boolean;
  onRevert: (auditId: string) => void;
  isReverting: boolean;
}

export default function ImportHistory({
  history,
  isLoading,
  canEdit,
  onRevert,
  isReverting,
}: ImportHistoryProps) {
  const [expanded, setExpanded] = useState(false);

  if (isLoading) return null;
  if (history.length === 0) return null;

  const formatAction = (action: string): string => {
    switch (action) {
      case 'bulk-import': return 'Bulk import';
      case 'bulk-edit': return 'Bulk edit';
      case 'revert': return 'Revert';
      default: return action;
    }
  };

  const formatDate = (timestamp: string): string => {
    const d = new Date(timestamp);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
      ' ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="card mt-4">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center justify-between w-full"
      >
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
          Import History
        </h3>
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {expanded && (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-dark-500">
                <th className="text-left py-2 px-3 text-xs font-medium text-gray-500">Date</th>
                <th className="text-left py-2 px-3 text-xs font-medium text-gray-500">User</th>
                <th className="text-left py-2 px-3 text-xs font-medium text-gray-500">Action</th>
                <th className="text-right py-2 px-3 text-xs font-medium text-gray-500">Sprints</th>
                {canEdit && <th className="text-right py-2 px-3 text-xs font-medium text-gray-500"></th>}
              </tr>
            </thead>
            <tbody>
              {history.map((record) => (
                <tr key={record.auditId} className="border-b border-gray-100 dark:border-dark-600">
                  <td className="py-2 px-3 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                    {formatDate(record.timestamp)}
                  </td>
                  <td className="py-2 px-3 text-gray-900 dark:text-white">
                    {record.userName}
                  </td>
                  <td className="py-2 px-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      record.action === 'revert'
                        ? 'bg-gray-100 dark:bg-dark-500 text-gray-600 dark:text-gray-300'
                        : record.action === 'bulk-import'
                          ? 'bg-accent-aquamarine/20 text-accent-aquamarine'
                          : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                    }`}>
                      {formatAction(record.action)}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-right text-gray-600 dark:text-gray-400">
                    {record.itemCount}
                  </td>
                  {canEdit && (
                    <td className="py-2 px-3 text-right">
                      <button
                        onClick={() => onRevert(record.auditId)}
                        disabled={isReverting}
                        className="text-xs font-medium text-primary-500 hover:text-primary-600 disabled:opacity-50"
                      >
                        Revert
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
